/**
 * Dashboard screen.
 * Route: /dashboard/overview
 * Composes setup and issue panels. Schedule math lives in logic/.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  HoverCard,
  Paper,
  Skeleton,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { fetchAllLinks, fetchAllTasks, type LocationTreeNode } from '../../api/schedulingApis';
import { loadTasksFromDB } from '../../stores/db';
import { useLocationsTreeStore } from '../../stores/locationsTreeStore';
import { usePortalProject } from '../../contexts/PortalProjectContext';
import { useDashboardSidebar } from '../../contexts/DashboardSidebarContext';
import { APP_ROUTES } from '../../navigation/appRoutes';
import {
  SCHEDULER_NAV_PERMISSIONS,
  useCanSchedulerNav,
} from '../../permissions/scheduler';
import {
  deleteDashboardInFlight,
  deleteDashboardMemoryCache,
  getDashboardInFlight,
  getDashboardMemoryCache,
  setDashboardInFlight,
  setDashboardMemoryCache,
  type DashboardData,
} from '../../dashboard/dashboardProjectCache';
import { deriveScheduleReadiness } from '../schedule-readiness';
import { parseDateSafe, resolveTaskName } from '../../utils/utils';
import { getDisplayUserName } from '../../utils/userDisplay';
import {
  getLeafTasks,
  prepareDashboardData,
  type DashboardDataset,
} from './logic/prepareDashboardData';
import { deriveOpenCriticalIssues } from './logic/deriveOpenCriticalIssues';
import { DashboardSCurveCard } from './components/DashboardSCurveCard';
import { OpenCriticalIssuesPanel } from './components/OpenCriticalIssuesPanel';
import {
  DashboardScheduleReadinessCallout,
  DashboardScheduleSetupEmpty,
} from './components/DashboardScheduleSetup';

/** Stable empty tree — avoid `?? []` in Zustand selectors (infinite rerenders). */
const EMPTY_LOCATION_TREE: LocationTreeNode[] = [];

type DashboardApiDataPromise = Promise<DashboardDataset>;

function asPercent(value: number | null | undefined): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  // Support either 0..1 or 0..100 payloads.
  const scaled = n <= 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, scaled));
}

function formatDate(value: string | Date | null | undefined): string {
  const d = value instanceof Date ? value : parseDateSafe(value ?? null);
  if (!d) return '-';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const DASHBOARD_CARD_RADIUS = 'sm';

const dashboardCtaStyles = {
  root: {
    backgroundColor: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    border: '1px solid var(--mantine-color-default-border)',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'var(--mantine-color-default-hover)',
    },
  },
} as const;

function KpiInfoCard({
  title,
  value,
  subtitle,
  info,
}: {
  title: string;
  value: React.ReactNode;
  subtitle: string;
  info: string;
}) {
  return (
    <Card withBorder radius={DASHBOARD_CARD_RADIUS} p="md" style={{ position: 'relative' }}>
      <CardInfoIcon title={title} info={info} absolute />
      <Text size="xs" c="dimmed" pr={28}>
        {title}
      </Text>
      <Title order={3}>{value}</Title>
      <Text size="xs" c="dimmed">
        {subtitle}
      </Text>
    </Card>
  );
}

function CardInfoIcon({
  title,
  info,
  absolute = false,
}: {
  title: string;
  info: string;
  absolute?: boolean;
}) {
  return (
    <HoverCard width={280} shadow="md" withArrow position="bottom-end" openDelay={150} closeDelay={80}>
      <HoverCard.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label={`About ${title}`}
          style={absolute ? { position: 'absolute', top: 8, right: 8 } : undefined}
        >
          <IconInfoCircle size={16} stroke={1.5} />
        </ActionIcon>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Text size="sm">{info}</Text>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

function TaskStatusLegendRow({
  color,
  label,
  count,
}: {
  color: string;
  label: string;
  count: number;
}) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Group gap={8} wrap="nowrap">
        <Box
          w={8}
          h={8}
          style={{ borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}
        />
        <Text size="sm">{label}</Text>
      </Group>
      <Text size="sm" c="dimmed">
        {count}
      </Text>
    </Group>
  );
}

function TaskStatusDonut({
  statusTotal,
  statusAngles,
}: {
  statusTotal: number;
  statusAngles: { notStarted: number; inProgress: number; completed: number };
}) {
  const ringBackground =
    statusTotal > 0
      ? `conic-gradient(
          var(--app-dashboard-not-started) 0deg ${statusAngles.notStarted}deg,
          var(--app-dashboard-in-progress) ${statusAngles.notStarted}deg ${statusAngles.notStarted + statusAngles.inProgress}deg,
          var(--app-dashboard-completed) ${statusAngles.notStarted + statusAngles.inProgress}deg 360deg
        )`
      : 'var(--mantine-color-gray-3)';

  return (
    <Box
      style={{
        position: 'relative',
        width: 120,
        height: 120,
        flexShrink: 0,
      }}
    >
      <Box
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: ringBackground,
        }}
      />
      <Box
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 76,
          height: 76,
          borderRadius: '50%',
          background: 'var(--mantine-color-body)',
          border: '1px solid var(--mantine-color-default-border)',
        }}
      />
    </Box>
  );
}

/**
 * Dashboard home — project schedule health snapshot.
 */
const DashboardLanding: React.FC = () => {
  const { projectId, projectName } = usePortalProject();
  const displayUserName = getDisplayUserName();
  const projectLabel = projectName || projectId || 'your project';
  const { setProjectWindowDates } = useDashboardSidebar();
  const canViewTaskSchedule = useCanSchedulerNav(SCHEDULER_NAV_PERMISSIONS.scheduleTasks);
  const fetchTreeIfNeeded = useLocationsTreeStore((s) => s.fetchTreeIfNeeded);
  const locationTree = useLocationsTreeStore((s) =>
    projectId ? (s.treeByProjectId[projectId] ?? EMPTY_LOCATION_TREE) : EMPTY_LOCATION_TREE,
  );
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<DashboardData>({ tasks: [], links: [] });
  const hasDataRef = React.useRef(false);

  React.useEffect(() => {
    hasDataRef.current = data.tasks.length > 0;
  }, [data.tasks.length]);

  const fetchFromApiDeduped = React.useCallback(async (): Promise<DashboardData> => {
    const existing = getDashboardInFlight(projectId);
    if (existing) {
      return existing;
    }
    const requestPromise: DashboardApiDataPromise = (async () => {
      const [tasksResponse, linksResponse] = await Promise.all([
        fetchAllTasks({ projectId }),
        fetchAllLinks({ projectId }).catch(() => ({ data: [] })),
      ]);
      const tasks = Array.isArray(tasksResponse?.data) ? tasksResponse.data : [];
      const links = Array.isArray(linksResponse?.data) ? linksResponse.data : [];
      const result = prepareDashboardData(tasks, links);
      setDashboardMemoryCache(projectId, result);
      return result;
    })();
    setDashboardInFlight(projectId, requestPromise);
    try {
      return await requestPromise;
    } finally {
      deleteDashboardInFlight(projectId);
    }
  }, [projectId]);

  const load = React.useCallback(
    async (opts?: { forceRefresh?: boolean }) => {
      const forceRefresh = !!opts?.forceRefresh;
      if (!projectId) {
        setData({ tasks: [], links: [] });
        setLoading(false);
        return;
      }

      if (!hasDataRef.current) {
        setLoading(true);
      } else if (forceRefresh) {
        setRefreshing(true);
      }
      setError(null);

      try {
        if (forceRefresh) {
          deleteDashboardMemoryCache(projectId);
        }

        const treePromise = fetchTreeIfNeeded(projectId);

        if (!forceRefresh) {
          const inMemory = getDashboardMemoryCache(projectId);
          if (inMemory && inMemory.tasks.length > 0) {
            setData(prepareDashboardData(inMemory.tasks, inMemory.links));
            await treePromise;
            return;
          }

          const fromDb = await loadTasksFromDB(projectId);
          if (fromDb?.tasks?.length) {
            const dbData = prepareDashboardData(fromDb.tasks, fromDb.links ?? []);
            setDashboardMemoryCache(projectId, dbData);
            setData(dbData);
            await treePromise;
            return;
          }
        }

        const [apiData] = await Promise.all([fetchFromApiDeduped(), treePromise]);
        setData(apiData);
      } catch {
        setError('Could not load dashboard metrics right now.');
        setData((prev) => (prev.tasks.length > 0 ? prev : { tasks: [], links: [] }));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [projectId, fetchFromApiDeduped, fetchTreeIfNeeded]
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  const summary = React.useMemo(() => {
    const now = new Date();
    const tasks = data.tasks;
    const leafTasks = getLeafTasks(tasks);
    const totalTasks = tasks.length;
    const totalLeafTasks = leafTasks.length;
    const completedTasks = leafTasks.filter((t) => asPercent(t.percentComplete) >= 100).length;
    const overdueTasks = leafTasks.filter((t) => {
      const finish = parseDateSafe(t.finish ?? null);
      return !!finish && finish < now && asPercent(t.percentComplete) < 100;
    }).length;
    const criticalTasks = leafTasks.filter((t) => !!t.critical).length;
    const milestoneCount = tasks.filter((t) => !!t.milestone).length;
    const avgProgress =
      totalLeafTasks > 0
        ? Math.round(
            leafTasks.reduce((acc, t) => acc + asPercent(t.percentComplete), 0) / totalLeafTasks
          )
        : 0;

    const inSevenDays = new Date();
    inSevenDays.setDate(now.getDate() + 7);
    const upcomingStarts = leafTasks.filter((t) => {
      const start = parseDateSafe(t.start ?? null);
      return !!start && start >= now && start <= inSevenDays;
    }).length;

    const milestoneDueSoon = tasks.filter((t) => {
      if (!t.milestone) return false;
      const finish = parseDateSafe(t.finish ?? null);
      return !!finish && finish >= now && finish <= inSevenDays;
    }).length;

    const withDates = leafTasks
      .map((t) => ({
        start: parseDateSafe(t.start ?? null),
        finish: parseDateSafe(t.finish ?? null),
      }))
      .filter((t) => !!t.start || !!t.finish);
    const earliestStart = withDates
      .map((t) => t.start)
      .filter(Boolean)
      .sort((a, b) => (a!.getTime() - b!.getTime()))[0];
    const latestFinish = withDates
      .map((t) => t.finish)
      .filter(Boolean)
      .sort((a, b) => (b!.getTime() - a!.getTime()))[0];

    const upcomingMilestones = tasks
      .filter((t) => t.milestone)
      .map((t) => ({ ...t, finishDate: parseDateSafe(t.finish ?? null) }))
      .filter((t) => !!t.finishDate)
      .sort((a, b) => a.finishDate!.getTime() - b.finishDate!.getTime())
      .slice(0, 6);

    const needsAttention = leafTasks
      .map((t) => ({
        ...t,
        finishDate: parseDateSafe(t.finish ?? null),
        progress: Math.round(asPercent(t.percentComplete)),
      }))
      .filter((t) => {
        if (!t.finishDate) return false;
        const dueSoon = t.finishDate >= now && t.finishDate <= inSevenDays;
        const overdue = t.finishDate < now;
        return (dueSoon || overdue) && t.progress < 80;
      })
      .sort((a, b) => a.finishDate!.getTime() - b.finishDate!.getTime())
      .slice(0, 6);

    const statusCounts = leafTasks.reduce(
      (acc, t) => {
        const hasActualStart = !!t.actualStart;
        const hasActualFinish = !!t.actualFinish;
        if (hasActualStart && hasActualFinish) {
          acc.completed += 1;
        } else if (hasActualStart && !hasActualFinish) {
          acc.inProgress += 1;
        } else {
          acc.notStarted += 1;
        }
        return acc;
      },
      { notStarted: 0, inProgress: 0, completed: 0 }
    );
    const statusTotal = statusCounts.notStarted + statusCounts.inProgress + statusCounts.completed;
    const notStartedAngle = statusTotal ? (statusCounts.notStarted / statusTotal) * 360 : 0;
    const inProgressAngle = statusTotal ? (statusCounts.inProgress / statusTotal) * 360 : 0;
    const completedAngle = statusTotal ? (statusCounts.completed / statusTotal) * 360 : 0;

    return {
      totalTasks,
      totalLeafTasks,
      completedTasks,
      overdueTasks,
      criticalTasks,
      milestoneCount,
      milestoneDueSoon,
      upcomingStarts,
      avgProgress,
      dependencyCount: data.links.length,
      earliestStart: earliestStart ? formatDate(earliestStart) : '-',
      latestFinish: latestFinish ? formatDate(latestFinish) : '-',
      upcomingMilestones,
      needsAttention,
      statusCounts,
      statusTotal,
      statusAngles: {
        notStarted: notStartedAngle,
        inProgress: inProgressAngle,
        completed: completedAngle,
      },
    };
  }, [data]);

  const openCriticalAnalytics = React.useMemo(
    () => deriveOpenCriticalIssues(data.tasks),
    [data.tasks],
  );

  const scheduleReadiness = React.useMemo(
    () => deriveScheduleReadiness(data.tasks, locationTree),
    [data.tasks, locationTree],
  );

  const showSetupEmpty =
    !loading && !error && scheduleReadiness.state === 'needs_tasks';
  const showReadinessCallout =
    !loading &&
    !error &&
    (scheduleReadiness.state === 'needs_locations' ||
      scheduleReadiness.state === 'needs_assignment');

  React.useEffect(() => {
    setProjectWindowDates({
      start: summary.earliestStart,
      finish: summary.latestFinish,
    });
  }, [summary.earliestStart, summary.latestFinish, setProjectWindowDates]);

  return (
    <Box style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <Box py="xl" px="md" style={{ width: '100%' }}>
          <Stack gap="xl">
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                  <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                    <Title order={2}>
                      {displayUserName ? `Hello, ${displayUserName}` : 'Hello'}
                    </Title>
                    <Text c="dimmed" size="sm">
                      {showSetupEmpty
                        ? `Set up ${projectLabel} to see progress and schedule health here.`
                        : `Schedule health and progress snapshot for ${projectLabel}.`}
                    </Text>
                  </Stack>
                  <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                    {canViewTaskSchedule && !showSetupEmpty ? (
                      <Button
                        component={Link}
                        to={APP_ROUTES.schedule.tasks}
                        variant="default"
                        styles={dashboardCtaStyles}
                      >
                        Open Task schedule
                      </Button>
                    ) : null}
                    <Button
                      variant="default"
                      onClick={() => void load({ forceRefresh: true })}
                      loading={loading || refreshing}
                      styles={dashboardCtaStyles}
                    >
                      {showSetupEmpty ? 'Refresh' : 'Refresh metrics'}
                    </Button>
                  </Group>
                </Group>

                {error ? (
                  <Paper radius={DASHBOARD_CARD_RADIUS} p="md" withBorder>
                    <Text c="red">{error}</Text>
                  </Paper>
                ) : null}

                {showSetupEmpty ? (
                  <DashboardScheduleSetupEmpty projectLabel={projectLabel} />
                ) : loading && data.tasks.length === 0 ? (
                  <>
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <Card key={`kpi-skeleton-${idx}`} withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                          <Skeleton height={10} width="45%" radius="xl" mb="md" />
                          <Skeleton height={28} width="35%" radius="sm" mb="sm" />
                          <Skeleton height={10} width="65%" radius="xl" />
                        </Card>
                      ))}
                    </SimpleGrid>

                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                      {Array.from({ length: 2 }).map((_, idx) => (
                        <Card key={`panel-skeleton-${idx}`} withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                          <Group justify="space-between" mb="sm">
                            <Skeleton height={18} width="40%" radius="xl" />
                            <Skeleton height={22} width={90} radius="xl" />
                          </Group>
                          <Skeleton height={10} width="55%" radius="xl" mb="md" />
                          <Stack gap="xs">
                            {Array.from({ length: 4 }).map((__, row) => (
                              <Group key={`panel-${idx}-row-${row}`} justify="space-between" wrap="nowrap">
                                <Skeleton height={12} width={`${58 + row * 8}%`} radius="xl" />
                                <Skeleton height={20} width={88} radius="xl" />
                              </Group>
                            ))}
                          </Stack>
                        </Card>
                      ))}
                    </SimpleGrid>

                  </>
                ) : (
                  <>
                    {showReadinessCallout &&
                    (scheduleReadiness.state === 'needs_locations' ||
                      scheduleReadiness.state === 'needs_assignment') ? (
                      <DashboardScheduleReadinessCallout
                        state={scheduleReadiness.state}
                        unassignedLeafCount={scheduleReadiness.unassignedLeafCount}
                        leafTaskCount={scheduleReadiness.leafTaskCount}
                      />
                    ) : null}

                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                      <KpiInfoCard
                        title="Total tasks"
                        value={summary.totalTasks}
                        subtitle={`${summary.totalLeafTasks} actionable`}
                        info="All tasks in the project schedule. Actionable counts leaf tasks only (tasks with no children), which drive progress and risk metrics."
                      />
                      <KpiInfoCard
                        title="Completion"
                        value={`${summary.avgProgress}%`}
                        subtitle={`${summary.completedTasks} tasks completed`}
                        info="Average percent complete across leaf tasks (unweighted). Completed counts leaf tasks at 100%."
                      />
                      <KpiInfoCard
                        title="Schedule risk"
                        value={summary.overdueTasks}
                        subtitle="overdue tasks"
                        info="Leaf tasks whose finish date is in the past and that are still under 100% complete."
                      />
                      <KpiInfoCard
                        title="Critical tasks"
                        value={summary.criticalTasks}
                        subtitle={`${summary.dependencyCount} dependencies mapped`}
                        info="Leaf tasks marked on the critical path (critical flag from the schedule). The subtitle is the total number of task dependency links in the project."
                      />
                    </SimpleGrid>

                    <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
                      <Card withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                        <Group justify="space-between" mb="sm" wrap="nowrap" gap="sm">
                          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Title order={4}>Upcoming milestones</Title>
                            <CardInfoIcon
                              title="Upcoming milestones"
                              info="Milestones sorted by finish date (up to 6 shown). The badge is the total milestone count; the subtitle counts milestones due within the next 7 days."
                            />
                          </Group>
                          <Badge variant="light">{summary.milestoneCount} total</Badge>
                        </Group>
                        <Text size="xs" c="dimmed" mb="sm">
                          {summary.milestoneDueSoon} due in next 7 days
                        </Text>
                        <Stack gap="xs">
                          {summary.upcomingMilestones.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              No milestones found.
                            </Text>
                          ) : (
                            summary.upcomingMilestones.map((task) => (
                              <Group key={task.id} justify="space-between" wrap="nowrap">
                                <Text size="sm" truncate style={{ maxWidth: '70%' }}>
                                  {resolveTaskName(task) || `Task ${task.id}`}
                                </Text>
                                <Badge variant="light" color="blue">
                                  {formatDate(task.finish)}
                                </Badge>
                              </Group>
                            ))
                          )}
                        </Stack>
                      </Card>

                      <Card withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                        <Group justify="space-between" mb="sm" wrap="nowrap" gap="sm">
                          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Title order={4}>Task status</Title>
                            <CardInfoIcon
                              title="Task status"
                              info="Leaf tasks grouped by actual dates: Not started (no actual start), In-progress (actual start, no actual finish), Completed (both actual start and finish)."
                            />
                          </Group>
                          <Badge variant="light">{summary.statusTotal} leaf tasks</Badge>
                        </Group>
                        <Group align="center" wrap="nowrap" gap="lg">
                          <TaskStatusDonut
                            statusTotal={summary.statusTotal}
                            statusAngles={summary.statusAngles}
                          />
                          <Stack gap={8} style={{ flex: 1 }}>
                            <TaskStatusLegendRow
                              color="var(--app-dashboard-not-started)"
                              label="Not started"
                              count={summary.statusCounts.notStarted}
                            />
                            <TaskStatusLegendRow
                              color="var(--app-dashboard-in-progress)"
                              label="In-progress"
                              count={summary.statusCounts.inProgress}
                            />
                            <TaskStatusLegendRow
                              color="var(--app-dashboard-completed)"
                              label="Completed"
                              count={summary.statusCounts.completed}
                            />
                          </Stack>
                        </Group>
                      </Card>

                      <Card withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                        <Group justify="space-between" mb="sm" wrap="nowrap" gap="sm">
                          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Title order={4}>Needs attention</Title>
                            <CardInfoIcon
                              title="Needs attention"
                              info="Leaf tasks that are overdue or due within 7 days and still under 80% complete (up to 6 shown). The badge is overdue leaf tasks; the subtitle counts leaf tasks that start within the next 7 days."
                            />
                          </Group>
                          <Badge variant="light" color="orange">
                            {summary.overdueTasks} overdue
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" mb="sm">
                          {summary.upcomingStarts} tasks start in next 7 days
                        </Text>
                        <Stack gap="xs">
                          {summary.needsAttention.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              No immediate task risks detected.
                            </Text>
                          ) : (
                            summary.needsAttention.map((task) => (
                              <Group key={task.id} justify="space-between" wrap="nowrap">
                                <Text size="sm" truncate style={{ maxWidth: '60%' }}>
                                  {resolveTaskName(task) || `Task ${task.id}`}
                                </Text>
                                <Group gap={6}>
                                  <Badge
                                    variant="light"
                                    color={task.finishDate && task.finishDate < new Date() ? 'red' : 'yellow'}
                                  >
                                    {task.finishDate && task.finishDate < new Date() ? 'Overdue' : 'Due soon'}
                                  </Badge>
                                  <Badge variant="outline">{task.progress}%</Badge>
                                </Group>
                              </Group>
                            ))
                          )}
                        </Stack>
                      </Card>
                    </SimpleGrid>
                  </>
                )}

                {!showSetupEmpty ? (
                  <>
                    <DashboardSCurveCard />
                    {loading && data.tasks.length === 0 ? (
                      <Card withBorder radius={DASHBOARD_CARD_RADIUS} p="md">
                        <Group justify="space-between" mb="sm">
                          <Skeleton height={18} width="35%" radius="xl" />
                          <Skeleton height={22} width={48} radius="xl" />
                        </Group>
                        <Skeleton height={10} width="45%" radius="xl" mb="md" />
                        <Skeleton height={200} radius="sm" />
                      </Card>
                    ) : (
                      <OpenCriticalIssuesPanel
                        analytics={openCriticalAnalytics}
                        loading={loading || refreshing}
                      />
                    )}
                  </>
                ) : null}
              </Stack>
      </Box>
    </Box>
  );
};

export default DashboardLanding;
